import { useRouter, isNavigationFailure, NavigationFailureType } from 'vue-router';
import { useUserStore } from '@/core/store/useUserStore';
import { useBillingGuard } from '@/composables/useBillingGuard';
import { useAuthAnalytics } from '@/core/analytics/hooks/useAuthAnalytics';

export function usePostAuthSessionHandler() {
  const router = useRouter();
  const userStore = useUserStore();
  const billingGuard = useBillingGuard();
  const authAnalytics = useAuthAnalytics();

  const completePostAuthSession = async ({
    session,
    redirect,
    redirectTarget,
    redirectPresent,
    authFlow,
    method,
    sourcePosition,
    sessionSource
  }) => {
    // session 建立失败：不导航、不 billing，返回稳定结果（不向上冒泡为 router 异常）
    try {
      await userStore.setUserSession(session);
    } catch (sessionError) {
      // 通用错误文案交由页面展示（与 AuthCallbackView 自身失败分支一致），不泄漏原始 error
      userStore.loginError = 'Authentication failed. Please try again.';
      authAnalytics.trackAuthRedirectCompleted({
        actionStatus: 'failed',
        reasonCode: 'session_setup_failed',
        sourcePosition,
        redirectTarget,
        redirectPresent,
        authFlow,
        method,
        sessionSource,
        errorCode: sessionError?.code || sessionError?.name
      });
      return { redirectSucceeded: false };
    }

    let redirectSucceeded = false;
    try {
      const navResult = await router.push(redirect);
      // 成功：undefined 或 duplicated（幂等成功）；aborted / cancelled 视为失败（方案 §4.4）
      const isNavFailure =
        isNavigationFailure(navResult) && navResult.type !== NavigationFailureType.duplicated;
      if (isNavFailure) {
        authAnalytics.trackAuthRedirectCompleted({
          actionStatus: 'failed',
          reasonCode: 'router_push_failed',
          sourcePosition,
          redirectTarget,
          redirectPresent,
          authFlow,
          method,
          sessionSource,
          errorCode: navResult?.error?.code || navResult?.error?.name
        });
      } else {
        redirectSucceeded = true;
        authAnalytics.trackAuthRedirectCompleted({
          actionStatus: 'success',
          sourcePosition,
          redirectTarget,
          redirectPresent,
          authFlow,
          method,
          sessionSource
        });
      }
    } catch (error) {
      authAnalytics.trackAuthRedirectCompleted({
        actionStatus: 'failed',
        reasonCode: 'router_push_failed',
        sourcePosition,
        redirectTarget,
        redirectPresent,
        authFlow,
        method,
        sessionSource,
        errorCode: error?.code || error?.name
      });
    }

    // billing 失败隔离：不影响已建立的认证结果（方案 §4.5）
    // 账单同步失败仅在 billing 域内部记录（checkPostLogin 已吞掉 syncStatus 异常），
    // 不通过 redirect 事件上报，避免同一次 redirect 同时出现 success + failed 污染登录漏斗。
    if (redirectSucceeded) {
      try {
        await billingGuard.checkPostLogin();
      } catch (billingError) {
        // 登录后账单同步失败在 billing 域自行记录，不污染认证结果
        console.warn('[post-auth] billing sync failed, authentication result preserved:', billingError);
      }
    }

    return { redirectSucceeded };
  };

  return { completePostAuthSession };
}
